/**
 * apiErrorMessage — pont entre les erreurs API bilingues
 * (`messages: { en, fr }`, ADR 0027 P-3) et la langue active i18next côté UI.
 *
 * Doctrine 9.K.14 (D58.3) :
 * - L'API produit déjà des erreurs bilingues self-contained (chunk 9.K.6→9.K.13).
 * - Le frontend humain garde i18next pour les textes UI normaux.
 * - Pour les erreurs API, on choisit `apiError.messages.{en,fr}` selon la langue
 *   active i18next.
 * - Rétrocompat : si `messages` est absent (payload legacy ou non encore migré),
 *   on retombe sur l'alias `apiError.message` (chaîne FR par défaut côté API).
 *   Cet alias sera retiré en chunk ultérieur (post-D58.1 MCP/tools).
 */

import { ERROR_CODES } from "@kollectivia/shared";

import { ApiException } from "./apiError";

/**
 * Locale supportée. Inline `"en" | "fr"` (PO-Q-B 9.K.14 — pas de type shared).
 */
export type Lang = "en" | "fr";

/**
 * Normalise une langue arbitraire (`i18n.resolvedLanguage` peut valoir
 * `"en-US"`, `"fr-CA"`, `undefined`, etc.) vers `"en" | "fr"`. Fallback `"fr"`
 * (langue par défaut Kollectivia, parité backend payload `message` alias).
 */
export function normalizeLang(input: string | undefined | null): Lang {
  if (!input) return "fr";
  const lower = input.toLowerCase();
  if (lower.startsWith("en")) return "en";
  if (lower.startsWith("fr")) return "fr";
  return "fr";
}

/**
 * Retourne un message d'erreur lisible utilisateur pour une erreur arbitraire.
 *
 * Branches (ordre de priorité) :
 * 1. `ApiException` avec `apiError.messages.{lang}` présent → bilingue API.
 * 2. `ApiException` legacy (alias `apiError.message` uniquement) → FR alias.
 * 3. `Error` JS générique → `err.message`.
 * 4. Inconnu → chaîne vide (l'appelant gère le fallback i18next, ex.
 *    `t("errors.generic")`).
 *
 * @param err   Erreur arbitraire (typiquement `mutation.error`, `query.error`,
 *              ou `catch (err)`).
 * @param lang  Langue active. Doit être `"en"` ou `"fr"`. Pour passer
 *              `i18n.resolvedLanguage` brut, utilise `normalizeLang()` d'abord.
 * @returns     Chaîne lisible. Vide si aucune source disponible (l'appelant
 *              fournit alors son propre fallback i18next).
 */
export function getApiErrorMessage(err: unknown, lang: Lang): string {
  if (err instanceof ApiException) {
    const messages = err.apiError.messages;
    if (messages && typeof messages[lang] === "string" && messages[lang].length > 0) {
      return messages[lang];
    }
    if (typeof err.apiError.message === "string" && err.apiError.message.length > 0) {
      return err.apiError.message;
    }
    return "";
  }
  if (err instanceof Error && typeof err.message === "string") {
    return err.message;
  }
  return "";
}

/**
 * Le message bilingue d'un code du registre — chunk 9.BJ.1.
 *
 * Un blocage d'envoi arrive sous forme de CODE, pas de phrase : `GET
 * /v1/quote-requests/:id/readiness` renvoie exactement ce que la route d'envoi
 * refuserait. L'écran le traduit ici, avec le même registre que celui qui a
 * produit le refus — donc jamais avec une phrase de son cru, qui finirait par
 * dire autre chose que le serveur.
 *
 * Renvoie `null` pour un code inconnu : mieux vaut n'afficher que les blocages
 * qu'on sait nommer que d'inventer un libellé pour celui qu'on ne connaît pas.
 */
export function messageDuCode(code: string, lang: Lang): string | null {
  const entree = (ERROR_CODES as Record<string, { messages: Record<Lang, string> } | undefined>)[
    code
  ];
  return entree?.messages[lang] ?? null;
}
